/**
 * Launch Activation API client — `GET/POST /api/projects/:projectId/launch/v1/activation[/intent]`.
 * Same conventions as launch-readiness: `VITE_BUILDER_API_URL` base + Firebase bearer token
 * (token via CoreFirebaseService, never firebase/auth directly), `{ success, data }` envelope.
 */
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ActivationIntent, ActivationProjection } from '@modules/shared-types';

export class LaunchActivationApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'LaunchActivationApiError';
    this.code = code;
    this.status = status;
  }
}

function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_BUILDER_API_URL;
  if (!url) throw new LaunchActivationApiError('VITE_BUILDER_API_URL is not configured', 'CONFIG_MISSING', 0);
  return url;
}

async function getAuthToken(): Promise<string> {
  const token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken();
  if (!token) throw new LaunchActivationApiError('Not authenticated', 'UNAUTHORIZED', 401);
  return token;
}

async function request<T>(projectId: string, suffix: string, init: { method: 'GET' | 'POST'; body?: unknown }): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(
    `${getApiBaseUrl()}/api/projects/${encodeURIComponent(projectId)}/launch/v1/activation${suffix}`,
    {
      method: init.method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) {
    throw new LaunchActivationApiError(body.error || body.message || `API error: ${response.status}`, body.code || 'UNKNOWN_ERROR', response.status);
  }
  return body.data as T;
}

export const launchActivationClient = {
  getActivation(projectId: string): Promise<ActivationProjection> {
    return request<ActivationProjection>(projectId, '', { method: 'GET' });
  },
  setIntent(projectId: string, intent: Omit<ActivationIntent, 'updatedAt'>): Promise<ActivationProjection> {
    return request<ActivationProjection>(projectId, '/intent', { method: 'POST', body: intent });
  },
};

export default launchActivationClient;
